import { useCallback, useEffect, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Typography from '@tiptap/extension-typography'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { Markdown } from 'tiptap-markdown'
import { Addition, Deletion, Note, parseAnnotations, serializeAnnotations } from '@/components/editor/AnnotationExtension'
import { EditorToolbar } from '@/components/editor/EditorToolbar'
import { TagInput } from '@/components/ui/TagInput'
import { useCpStore, type ImageAttachment } from '@/stores/useCpStore'
import { streamCpCardIngestion } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Upload, Sparkles, X, ImageIcon, Loader2 } from 'lucide-react'

export function CpEditor() {
  const {
    sessionId,
    content,
    setContent,
    title,
    setTitle,
    tags,
    setTags,
    filename,
    setFilename,
    attachedImages,
    addAttachedImage,
    removeAttachedImage,
    clearEditor,
    currentWorkflow,
    queueSubmission,
    startWorkflow,
    updateWorkflowProgress,
    finishWorkflow,
    failWorkflow,
    processNextInQueue,
    workflowQueue,
  } = useCpStore()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const isUpdatingRef = useRef(false)
  const isProcessing = currentWorkflow !== null

  // TipTap editor configuration
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      Markdown.configure({
        html: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      Placeholder.configure({
        placeholder: 'Paste your content here or paste a screenshot...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
      Table.configure({
        resizable: false,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Addition,
      Deletion,
      Note,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose-zinsser outline-none min-h-full',
      },
      handlePaste: (_view, event) => {
        // Handle image paste
        const items = event.clipboardData?.items
        if (!items) return false

        const imageItems: DataTransferItem[] = []
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.startsWith('image/')) {
            imageItems.push(items[i])
          }
        }

        if (imageItems.length === 0) return false

        // Process each image
        imageItems.forEach((item, index) => {
          const file = item.getAsFile()
          if (!file) return

          const reader = new FileReader()
          reader.onload = () => {
            const dataUrl = reader.result as string
            const base64Data = dataUrl.split(',')[1]
            const mimeType = file.type || 'image/png'

            addAttachedImage({
              data: base64Data,
              mimeType,
              name: file.name || `pasted-image-${Date.now()}-${index}.${mimeType.split('/')[1]}`,
            })
          }
          reader.readAsDataURL(file)
        })

        return true // Prevent default paste for images
      },
    },
    onUpdate: ({ editor }) => {
      if (isUpdatingRef.current) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const storage = editor.storage as any
      if (storage?.markdown?.getMarkdown) {
        const markdown = storage.markdown.getMarkdown()
        const serialized = serializeAnnotations(markdown)
        setContent(serialized)
      }
    },
    immediatelyRender: false,
  })

  // Update editor when content changes externally (e.g., file upload)
  useEffect(() => {
    if (!editor || isUpdatingRef.current) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor.storage as any
    const currentContent = storage?.markdown?.getMarkdown?.() || ''

    if (content !== currentContent) {
      isUpdatingRef.current = true
      if (content) {
        const parsedContent = parseAnnotations(content)
        editor.commands.setContent(parsedContent)
      } else {
        editor.commands.clearContent()
      }
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 10)
    }
  }, [editor, content])

  // Process workflow with SSE streaming
  const runWorkflow = useCallback(async (submission: {
    content: string
    title: string
    tags: string[]
    filename: string | null
    images: ImageAttachment[]
  }) => {
    try {
      await streamCpCardIngestion(
        {
          session_id: sessionId || undefined,
          raw_text: submission.content,
          title: submission.title || undefined,
          tags: submission.tags.length > 0 ? submission.tags : undefined,
        },
        submission.images.length > 0
          ? new File(
              [Uint8Array.from(atob(submission.images[0].data), (c) => c.charCodeAt(0))],
              submission.images[0].name,
              { type: submission.images[0].mimeType }
            )
          : null,
        {
          onIngestStart: () => {
            updateWorkflowProgress({ stage: 'ingest', message: 'Ingesting content...' })
          },
          onIngestComplete: () => {
            updateWorkflowProgress({ stage: 'ingest', message: 'Content ingested' })
          },
          onDuplicateDetected: () => {
            updateWorkflowProgress({ message: 'Duplicate source detected' })
          },
          onCardifyStart: () => {
            updateWorkflowProgress({ stage: 'cardify', message: 'Extracting cards...' })
          },
          onCardProposed: (_card, current, total) => {
            updateWorkflowProgress({
              stage: 'cardify',
              message: `Extracting card ${current} of ${total}...`,
              cardsProposed: current,
              cardsTotal: total,
            })
          },
          onCardifyComplete: (count) => {
            updateWorkflowProgress({
              stage: 'cardify',
              message: `Extracted ${count} cards`,
              cardsProposed: count,
              cardsTotal: count,
            })
          },
          onEmbedStart: () => {
            updateWorkflowProgress({ stage: 'embed', message: 'Generating embeddings...' })
          },
          onEmbedComplete: () => {
            updateWorkflowProgress({ stage: 'embed', message: 'Embeddings generated' })
          },
          onCanonicalizeStart: () => {
            updateWorkflowProgress({ stage: 'canonicalize', message: 'Finding duplicates...' })
          },
          onCanonicalizeDecision: (_card, current, total) => {
            updateWorkflowProgress({
              stage: 'canonicalize',
              message: `Processing card ${current} of ${total}...`,
              cardsProcessed: current,
            })
          },
          onCanonicalizeComplete: (newCount, _dupCount) => {
            updateWorkflowProgress({
              stage: 'canonicalize',
              message: `${newCount} new cards`,
              cardsProcessed: newCount,
            })
          },
          onLinkStart: () => {
            updateWorkflowProgress({ stage: 'link', message: 'Creating links...' })
          },
          onLinkCreated: () => {
            // Could track edge count here
          },
          onLinkComplete: (edgesCreated) => {
            updateWorkflowProgress({
              stage: 'link',
              message: `Created ${edgesCreated} links`,
              edgesCreated,
            })
          },
          onDone: (summary) => {
            // Transform API response to CardIngestionSummary format
            finishWorkflow({
              source_id: summary.source_id,
              is_duplicate: false,
              cards_created: summary.cards_created,
              cards_duplicate: summary.cards_duplicate,
              cards_related: 0,
              edges_created: summary.edges_created,
              cards: summary.card_ids.map((id) => ({
                card_id: id,
                title: '',
                type: '',
                domain: '',
                is_new: true,
                decision: 'new' as const,
              })),
            })
            // Process next in queue after a short delay
            setTimeout(() => {
              const next = processNextInQueue()
              if (next) {
                runWorkflow(next)
              }
            }, 100)
          },
          onError: (error) => {
            failWorkflow(error)
            // Still try to process next in queue
            setTimeout(() => {
              const next = processNextInQueue()
              if (next) {
                runWorkflow(next)
              }
            }, 100)
          },
        }
      )
    } catch (e) {
      failWorkflow(e instanceof Error ? e.message : 'Failed to process')
      // Try next in queue
      setTimeout(() => {
        const next = processNextInQueue()
        if (next) {
          runWorkflow(next)
        }
      }, 100)
    }
  }, [sessionId, updateWorkflowProgress, finishWorkflow, failWorkflow, processNextInQueue])

  const handleSubmit = useCallback(async () => {
    const hasContent = content.trim() || attachedImages.length > 0
    if (!hasContent) return

    // Capture current state before clearing
    const submission = {
      content: content.trim(),
      title: title.trim(),
      tags: [...tags],
      filename,
      images: [...attachedImages],
    }

    // Clear editor immediately (non-blocking UX)
    clearEditor()
    if (editor) {
      editor.commands.clearContent()
    }

    // If no workflow is currently running, run immediately
    if (!currentWorkflow) {
      // Generate ID and start workflow
      const submissionId = `sub_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
      startWorkflow(submissionId)
      // Run with the captured submission directly
      runWorkflow(submission)
    } else {
      // Workflow in progress - queue it for later
      queueSubmission(submission)
    }
  }, [
    content,
    title,
    tags,
    filename,
    attachedImages,
    clearEditor,
    editor,
    queueSubmission,
    currentWorkflow,
    startWorkflow,
    runWorkflow,
  ])

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      // Check if it's an image
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = () => {
          const dataUrl = reader.result as string
          const base64Data = dataUrl.split(',')[1]
          addAttachedImage({
            data: base64Data,
            mimeType: file.type,
            name: file.name,
          })
        }
        reader.readAsDataURL(file)
      } else {
        // Text file - read content into editor
        setFilename(file.name)
        const reader = new FileReader()
        reader.onload = (event) => {
          const text = event.target?.result as string
          setContent(text)
          if (editor) {
            editor.commands.setContent(text)
          }
        }
        reader.readAsText(file)
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [setContent, setFilename, editor, addAttachedImage]
  )

  const canSubmit = content.trim() || attachedImages.length > 0
  const queueCount = workflowQueue.length

  return (
    <div className="relative flex flex-col h-full overflow-hidden">
      {/* Header with title and tags */}
      <div className="flex-shrink-0 bg-background border-b border-border">
        <div className="px-4 md:px-6 pt-4 md:pt-6 pb-3 space-y-2">
          {/* Title input */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Source title (optional - AI will generate if blank)"
            className={cn(
              'bg-transparent border-none outline-none w-full',
              'text-xl font-light',
              'text-foreground placeholder:text-muted-foreground'
            )}
          />
          {/* Tags input */}
          <TagInput
            tags={tags}
            onTagsChange={setTags}
            placeholder="#tags (persists for session)"
          />
        </div>
      </div>

      {/* Image attachments preview */}
      {attachedImages.length > 0 && (
        <div className="flex-shrink-0 px-4 md:px-6 py-3 border-b border-border bg-muted/30">
          <div className="flex flex-wrap gap-2">
            {attachedImages.map((img, index) => (
              <div
                key={index}
                className="relative group w-16 h-16 rounded-md overflow-hidden border border-border bg-muted"
              >
                <img
                  src={`data:${img.mimeType};base64,${img.data}`}
                  alt={img.name}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeAttachedImage(index)}
                  className="absolute top-0.5 right-0.5 p-0.5 bg-background/80 rounded-full text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scrollable editor area */}
      <div
        className="flex-1 overflow-y-auto overscroll-contain cursor-text"
        onClick={() => editor?.commands.focus()}
      >
        <div className="px-4 md:px-6 py-6 md:py-8 pb-[50vh] md:pb-[40vh] min-h-full">
          <div className="max-w-3xl">
            <EditorContent editor={editor} className="min-h-full" />
          </div>
        </div>
      </div>

      {/* Editor toolbar */}
      <EditorToolbar editor={editor} disabled={false} actionBarHeight={72} />

      {/* Bottom action bar */}
      <div className="flex-shrink-0 bg-background border-t border-border">
        <div className="px-4 md:px-6 py-4">
          <div className="flex items-center gap-3">
            {/* Upload button */}
            <label className="cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                accept=".txt,.md,.markdown,.png,.jpg,.jpeg,.webp,.pdf"
                className="hidden"
              />
              <span
                className={cn(
                  'inline-flex items-center gap-2',
                  'px-4 py-2.5 text-sm font-light',
                  'border border-solid border-border rounded-md',
                  'text-foreground hover:bg-muted/50',
                  'transition-colors cursor-pointer'
                )}
              >
                {attachedImages.length > 0 ? (
                  <ImageIcon className="w-4 h-4" strokeWidth={1.5} />
                ) : (
                  <Upload className="w-4 h-4" strokeWidth={1.5} />
                )}
                Upload
              </span>
            </label>

            {/* Extract button */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={cn(
                'inline-flex items-center gap-2',
                'px-4 py-2.5 text-sm font-light',
                'bg-foreground text-background rounded-md',
                'hover:bg-foreground/90',
                'transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  {queueCount > 0 ? `Queue (${queueCount})` : 'Processing...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  Extract Cards
                </>
              )}
            </button>

            {/* Queue indicator */}
            {queueCount > 0 && !isProcessing && (
              <span className="text-sm text-muted-foreground">
                {queueCount} queued
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
